import { getCookie, setCookie } from '@/utils/cookie'
import { initializeMatomoVisitor } from '@/utils/matomoVisitor'

type GroupName = string

interface ExperimentGroupConfig {
  ratio: number
}

interface ExperimentResponse {
  experimentName: string
  group: Record<GroupName, ExperimentGroupConfig>
  expiresTime: string | number
  default?: GroupName
}

const DEFAULT_GROUP = 'A'
const FALLBACK_COOKIE_DURATION = 30 * 60 * 1000
const DAY_DURATION = 24 * 60 * 60 * 1000
const PARITY_SWAP_START_DAY = Date.UTC(2024, 6, 22)
const AB_GROUP_COOKIE = 'X-Service-Group-AB'
const AB_EXPERIMENT_COOKIE = 'X-AB-Experiment-Name'

const pendingExperiments = new Map<string, Promise<GroupName>>()

const getFallbackExpiresTime = () => Date.now() + FALLBACK_COOKIE_DURATION

const getCookieOptions = (expiresTime: number) => ({
  expires: new Date(expiresTime),
  sameSite: 'lax' as const,
  secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
})

// 仅在生产域名上传错误日志。
function uploadLog(requestData: Record<string, unknown>) {
  if (
    typeof window === 'undefined' ||
    window.location.hostname !== 'www.crazysales.com.au'
  ) {
    return
  }

  void fetch('https://www.crazysales.com.au/fp/log', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  }).then((response) => {
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    return response.json()
  }).catch((error: unknown) => {
    console.error(error)
  })
}

// 新访客首次分组后通知后端。
function uploadFirstData() {
  if (typeof window === 'undefined') {
    return
  }

  const currentUrl = encodeURIComponent(window.location.href)
  void fetch(`/tracking/dataAnalysis?URL=${currentUrl}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((response) => {
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    return response.json()
  }).catch((error: unknown) => {
    console.error(error)
  })
}

const allocateBucket = (
  groups: Array<[GroupName, ExperimentGroupConfig]>,
  bucketId: number,
): GroupName | undefined => {
  let upperBound = 0

  for (const [groupName, config] of groups) {
    upperBound += config.ratio
    if (bucketId < upperBound) {
      return groupName
    }
  }

  return undefined
}

const hash = (uid: string, salt: number) => {
  let h1 = 0xdeadbeef ^ salt
  let h2 = 0x41c6ce57 ^ salt

  for (let i = 0, ch; i < uid.length; i++) {
    ch = uid.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)

  return 4294967296 * (2097151 & h2) + (h1 >>> 0)
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const parseExperimentResponse = (
  value: unknown,
  requestedExperimentName: string,
): ExperimentResponse => {
  if (!isRecord(value)) {
    throw new Error('AB test config is not an object')
  }

  const { experimentName, group, expiresTime } = value
  if (typeof experimentName !== 'string' || experimentName !== requestedExperimentName) {
    throw new Error('AB test experiment name does not match the request')
  }

  if (!isRecord(group)) {
    throw new Error('AB test group config is invalid')
  }

  const normalizedGroup: Record<GroupName, ExperimentGroupConfig> = {}
  let totalRatio = 0

  for (const [groupName, config] of Object.entries(group)) {
    if (!isRecord(config) || typeof config.ratio !== 'number') {
      throw new Error(`AB test ratio for group ${groupName} is invalid`)
    }

    if (!Number.isFinite(config.ratio) || config.ratio <= 0) {
      throw new Error(`AB test ratio for group ${groupName} must be positive`)
    }

    normalizedGroup[groupName] = { ratio: config.ratio }
    totalRatio += config.ratio
  }

  if (Object.keys(normalizedGroup).length === 0) {
    throw new Error('AB test group config is empty')
  }

  if (Math.abs(totalRatio - 100) > Number.EPSILON) {
    throw new Error(`AB test ratios must total 100, received ${totalRatio}`)
  }

  if (
    (typeof expiresTime !== 'string' && typeof expiresTime !== 'number') ||
    !Number.isFinite(new Date(expiresTime).getTime())
  ) {
    throw new Error('AB test expiration time is invalid')
  }

  const defaultGroup = typeof value.default === 'string'
    ? value.default
    : DEFAULT_GROUP

  if (!normalizedGroup[defaultGroup]) {
    throw new Error(`AB test default group ${defaultGroup} is not configured`)
  }

  return {
    experimentName,
    group: normalizedGroup,
    expiresTime,
    default: defaultGroup,
  }
}

const getSydneyCalendarDay = () => {
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date())

    const dateParts: Record<string, number> = {}
    for (const part of parts) {
      if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
        dateParts[part.type] = Number(part.value)
      }
    }

    if (dateParts.year && dateParts.month && dateParts.day) {
      return Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day)
    }
  } catch (error) {
    console.error(error)
  }

  const now = new Date()
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
}

const applyParitySwap = (bucket: GroupName): GroupName => {
  const elapsedDays = Math.floor(
    (getSydneyCalendarDay() - PARITY_SWAP_START_DAY) / DAY_DURATION,
  )

  if (elapsedDays % 2 !== 0) {
    return bucket
  }

  if (bucket === 'A') {
    return 'B'
  }
  if (bucket === 'B') {
    return 'A'
  }
  return bucket
}

function settingCookie(
  expiresTime: number,
  experimentName: string,
  bucket: GroupName,
  shouldApplyParitySwap = true,
): GroupName {
  const finalGroup = shouldApplyParitySwap ? applyParitySwap(bucket) : bucket
  const options = getCookieOptions(expiresTime)

  setCookie(AB_EXPERIMENT_COOKIE, experimentName, options)
  setCookie(AB_GROUP_COOKIE, finalGroup, options)

  return finalGroup
}

const setFallbackGroup = (
  experimentName: string,
  defaultGroup = DEFAULT_GROUP,
) => {
  return settingCookie(
    getFallbackExpiresTime(),
    experimentName,
    defaultGroup,
    false,
  )
}

const calculate = (
  visitorId: string,
  response: ExperimentResponse,
  isNewVisitor: boolean,
): GroupName => {
  const groups = Object.entries(response.group)
  const hashInput = `${response.experimentName}:${visitorId}`
  const bucketId = hash(hashInput, groups.length) % 100
  const bucket = allocateBucket(groups, bucketId)
  const expiresTime = new Date(response.expiresTime).getTime()
  const defaultGroup = response.default ?? DEFAULT_GROUP

  let finalGroup: GroupName
  if (expiresTime > Date.now() && bucket) {
    finalGroup = settingCookie(expiresTime, response.experimentName, bucket)
  } else {
    finalGroup = setFallbackGroup(response.experimentName, defaultGroup)
    uploadLog({
      conf: JSON.stringify({ expiresTime, nowTime: Date.now(), bucket }),
      bucketId: 'calculate-error',
      experimentName: response.experimentName,
    })
  }

  if (isNewVisitor) {
    uploadFirstData()
  }

  return finalGroup
}

const getCachedGroup = (experimentName: string): GroupName | undefined => {
  const serviceGroup = getCookie(AB_GROUP_COOKIE)
  const cachedExperimentName = getCookie(AB_EXPERIMENT_COOKIE)

  if (!serviceGroup || cachedExperimentName !== experimentName) {
    return undefined
  }

  return serviceGroup
}

const getGroup = async (
  name: string,
  visitorId: string,
  isNewVisitor: boolean,
): Promise<GroupName> => {
  let defaultGroup = DEFAULT_GROUP

  try {
    const data = await fetch(
      `/others/getABTestConfig.php?name=${encodeURIComponent(name)}`,
      {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: '*/*',
        },
        mode: 'cors',
      },
    )

    if (!data.ok) {
      throw new Error(`请求出错：${data.statusText}`)
    }

    const rawResponse: unknown = await data.json()
    if (isRecord(rawResponse) && typeof rawResponse.default === 'string') {
      defaultGroup = rawResponse.default
    }

    const response = parseExperimentResponse(rawResponse, name)
    return calculate(visitorId, response, isNewVisitor)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    const fallbackGroup = setFallbackGroup(name, defaultGroup)
    uploadLog({
      conf: message,
      bucketId: 'getABTestConfig-error',
      experimentName: name,
    })
    return fallbackGroup
  }
}

const runExperiment = async (name: string): Promise<GroupName> => {
  try {
    const { visitorId, isNewVisitor } = initializeMatomoVisitor()

    const cachedGroup = getCachedGroup(name)
    if (cachedGroup) {
      return cachedGroup
    }

    return await getGroup(name, visitorId, isNewVisitor)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    const fallbackGroup = setFallbackGroup(name)
    uploadLog({
      conf: message,
      bucketId: 'abTestCalculate-error',
      experimentName: name,
    })
    return fallbackGroup
  }
}

export const abTestCalculate = (name: string): Promise<GroupName> => {
  if (!name || typeof window === 'undefined') {
    return Promise.resolve(DEFAULT_GROUP)
  }

  const pendingExperiment = pendingExperiments.get(name)
  if (pendingExperiment) {
    return pendingExperiment
  }

  const experiment = runExperiment(name).finally(() => {
    pendingExperiments.delete(name)
  })
  pendingExperiments.set(name, experiment)

  return experiment
}
